/**
 * Status → badge mapping shared by Overview / Documents / Connectors —
 * one vocabulary, one tone AND one icon per state (state is never encoded
 * by color alone), Lingui labels.
 *
 * DEFENSIVE by contract: the console now runs against the REAL backend,
 * whose status vocabularies can drift from the FE unions (that exact
 * drift crashed Overview with "Cannot read properties of undefined
 * (reading 'icon')"). Every lookup goes through a safe accessor that
 * falls back to a generic icon + the raw wire value as label — an
 * unknown status renders as a neutral chip, it NEVER throws.
 */

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { LucideIcon } from "lucide-react";
import {
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleHelp,
  CirclePause,
  CircleX,
  Clock,
  Crown,
  Mail,
  RefreshCw,
  Send,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  User,
} from "lucide-react";
import type * as React from "react";

import { ConBadge, type ConBadgeTone } from "./atoms";

interface StatusMeta {
  /** Lingui label for known statuses; ``null`` → render ``raw`` as-is. */
  label: MessageDescriptor | null;
  /** The raw wire value — the fallback label for unknown statuses. */
  raw: string;
  tone: ConBadgeTone;
  icon: LucideIcon;
}

type KnownMeta = { label: MessageDescriptor; tone: ConBadgeTone; icon: LucideIcon };

/**
 * Safe lookup: returns the known meta for ``status`` or a neutral
 * fallback (generic icon, raw value as label). Handles non-string /
 * empty values coming from a loosely-typed backend payload too.
 */
function resolveStatusMeta(map: Record<string, KnownMeta>, status: unknown): StatusMeta {
  const known = typeof status === "string" && Object.hasOwn(map, status) ? map[status] : undefined;
  if (known !== undefined && typeof status === "string") {
    return { label: known.label, raw: status, tone: known.tone, icon: known.icon };
  }
  const raw = typeof status === "string" && status.trim().length > 0 ? status : "—";
  return { label: null, raw, tone: "neutral", icon: CircleHelp };
}

function StatusBadge({ meta }: { meta: StatusMeta }): React.ReactElement {
  const { i18n } = useLingui();
  const Icon = meta.icon;
  return (
    <ConBadge tone={meta.tone}>
      <Icon size={11} strokeWidth={2} aria-hidden className="shrink-0" />
      {meta.label ? i18n._(meta.label) : <span className="font-mono">{meta.raw}</span>}
    </ConBadge>
  );
}

// ── Job status ────────────────────────────────────────────────────────────

const JOB_STATUS_META: Record<string, KnownMeta> = {
  queued: { label: msg`In coda`, tone: "neutral", icon: Clock },
  pending: { label: msg`In coda`, tone: "neutral", icon: Clock },
  running: { label: msg`In corso`, tone: "accent", icon: RefreshCw },
  processing: { label: msg`In corso`, tone: "accent", icon: RefreshCw },
  completed: { label: msg`Completato`, tone: "success", icon: CircleCheck },
  succeeded: { label: msg`Completato`, tone: "success", icon: CircleCheck },
  success: { label: msg`Completato`, tone: "success", icon: CircleCheck },
  failed: { label: msg`Fallito`, tone: "danger", icon: CircleX },
  error: { label: msg`Fallito`, tone: "danger", icon: CircleX },
};

/** Exported for tests — never returns ``undefined``. */
export function getJobStatusMeta(status: unknown): StatusMeta {
  return resolveStatusMeta(JOB_STATUS_META, status);
}

export function JobStatusBadge({ status }: { status: string }): React.ReactElement {
  return <StatusBadge meta={getJobStatusMeta(status)} />;
}

// ── Sync status ───────────────────────────────────────────────────────────

const SYNC_STATUS_META: Record<string, KnownMeta> = {
  ok: { label: msg`OK`, tone: "success", icon: CircleCheck },
  success: { label: msg`OK`, tone: "success", icon: CircleCheck },
  completed: { label: msg`OK`, tone: "success", icon: CircleCheck },
  running: { label: msg`In corso`, tone: "accent", icon: RefreshCw },
  syncing: { label: msg`In corso`, tone: "accent", icon: RefreshCw },
  processing: { label: msg`In corso`, tone: "accent", icon: RefreshCw },
  pending: { label: msg`In attesa`, tone: "neutral", icon: Clock },
  idle: { label: msg`Pronto`, tone: "neutral", icon: CircleCheck },
  not_started: { label: msg`Non avviata`, tone: "neutral", icon: CircleDashed },
  paused: { label: msg`In pausa`, tone: "neutral", icon: CirclePause },
  error: { label: msg`Errore`, tone: "danger", icon: CircleAlert },
  failed: { label: msg`Errore`, tone: "danger", icon: CircleAlert },
};

/** Exported for tests — never returns ``undefined``. */
export function getSyncStatusMeta(status: unknown): StatusMeta {
  return resolveStatusMeta(SYNC_STATUS_META, status);
}

export function SyncStatusBadge({ status }: { status: string }): React.ReactElement {
  return <StatusBadge meta={getSyncStatusMeta(status)} />;
}

// ── Connector status ──────────────────────────────────────────────────────

const CONNECTOR_STATUS_META: Record<string, KnownMeta> = {
  connected: { label: msg`Connesso`, tone: "success", icon: CircleCheck },
  active: { label: msg`Connesso`, tone: "success", icon: CircleCheck },
  not_connected: { label: msg`Non connesso`, tone: "neutral", icon: CircleDashed },
  disconnected: { label: msg`Non connesso`, tone: "neutral", icon: CircleDashed },
  error: { label: msg`Errore`, tone: "danger", icon: CircleAlert },
};

/** Exported for tests — never returns ``undefined``. */
export function getConnectorStatusMeta(status: unknown): StatusMeta {
  return resolveStatusMeta(CONNECTOR_STATUS_META, status);
}

export function ConnectorStatusBadge({ status }: { status: string }): React.ReactElement {
  return <StatusBadge meta={getConnectorStatusMeta(status)} />;
}

// ── Action risk (custom-connector write-loop) ──────────────────────────────

const ACTION_RISK_META: Record<string, KnownMeta> = {
  low: { label: msg`Rischio basso`, tone: "neutral", icon: ShieldCheck },
  medium: { label: msg`Rischio medio`, tone: "warning", icon: ShieldAlert },
  high: { label: msg`Rischio alto`, tone: "danger", icon: ShieldAlert },
  critical: { label: msg`Rischio critico`, tone: "danger", icon: ShieldX },
};

/** Exported for tests — never returns ``undefined``. */
export function getActionRiskMeta(risk: unknown): StatusMeta {
  return resolveStatusMeta(ACTION_RISK_META, risk);
}

export function ActionRiskBadge({ risk }: { risk: string }): React.ReactElement {
  return <StatusBadge meta={getActionRiskMeta(risk)} />;
}

// ── Action request lifecycle (proposed → dispatched → completed) ───────────

const ACTION_REQUEST_STATUS_META: Record<string, KnownMeta> = {
  proposed: { label: msg`Proposta`, tone: "neutral", icon: CircleDashed },
  dispatched: { label: msg`Inviata`, tone: "accent", icon: Send },
  completed: { label: msg`Completata`, tone: "success", icon: CircleCheck },
  failed: { label: msg`Fallita`, tone: "danger", icon: CircleX },
  canceled: { label: msg`Annullata`, tone: "neutral", icon: CircleAlert },
};

/** Exported for tests — never returns ``undefined``. */
export function getActionRequestStatusMeta(status: unknown): StatusMeta {
  return resolveStatusMeta(ACTION_REQUEST_STATUS_META, status);
}

export function ActionRequestStatusBadge({ status }: { status: string }): React.ReactElement {
  return <StatusBadge meta={getActionRequestStatusMeta(status)} />;
}

// ── Flow lifecycle (draft → active ⇄ paused → archived) ────────────────────

const FLOW_STATUS_META: Record<string, KnownMeta> = {
  draft: { label: msg`Bozza`, tone: "neutral", icon: CircleDashed },
  active: { label: msg`Attivo`, tone: "success", icon: CircleCheck },
  paused: { label: msg`In pausa`, tone: "warning", icon: CirclePause },
  archived: { label: msg`Archiviato`, tone: "neutral", icon: CircleX },
};

/** Exported for tests — never returns ``undefined``. */
export function getFlowStatusMeta(status: unknown): StatusMeta {
  return resolveStatusMeta(FLOW_STATUS_META, status);
}

export function FlowStatusBadge({ status }: { status: string }): React.ReactElement {
  return <StatusBadge meta={getFlowStatusMeta(status)} />;
}

// ── Flow run outcome (a console-triggered manual/test run) ─────────────────

const FLOW_RUN_STATUS_META: Record<string, KnownMeta> = {
  pending: { label: msg`In coda`, tone: "neutral", icon: Clock },
  running: { label: msg`In corso`, tone: "accent", icon: RefreshCw },
  success: { label: msg`Completato`, tone: "success", icon: CircleCheck },
  failed: { label: msg`Fallito`, tone: "danger", icon: CircleX },
  pending_approval: { label: msg`In attesa di approvazione`, tone: "warning", icon: Clock },
};

/** Exported for tests — never returns ``undefined``. */
export function getFlowRunStatusMeta(status: unknown): StatusMeta {
  return resolveStatusMeta(FLOW_RUN_STATUS_META, status);
}

export function FlowRunStatusBadge({ status }: { status: string }): React.ReactElement {
  return <StatusBadge meta={getFlowRunStatusMeta(status)} />;
}

// ── Org member role (owner / admin / member) ───────────────────────────────

// Customer-facing labels — "admin" is Ando-internal vocabulary, never shown:
// owner → Proprietario, admin → Gestore, member → Sviluppatore.
const MEMBER_ROLE_META: Record<string, KnownMeta> = {
  owner: { label: msg`Proprietario`, tone: "accent", icon: Crown },
  admin: { label: msg`Gestore`, tone: "neutral", icon: ShieldCheck },
  member: { label: msg`Sviluppatore`, tone: "neutral", icon: User },
};

/** Exported for tests — never returns ``undefined``. */
export function getMemberRoleMeta(role: unknown): StatusMeta {
  return resolveStatusMeta(MEMBER_ROLE_META, role);
}

export function MemberRoleBadge({ role }: { role: string }): React.ReactElement {
  return <StatusBadge meta={getMemberRoleMeta(role)} />;
}

// ── Org member lifecycle (active / invited / suspended) ────────────────────

const MEMBER_STATUS_META: Record<string, KnownMeta> = {
  active: { label: msg`Attivo`, tone: "success", icon: CircleCheck },
  invited: { label: msg`Invitato`, tone: "warning", icon: Mail },
  suspended: { label: msg`Sospeso`, tone: "neutral", icon: CirclePause },
};

/** Exported for tests — never returns ``undefined``. */
export function getMemberStatusMeta(status: unknown): StatusMeta {
  return resolveStatusMeta(MEMBER_STATUS_META, status);
}

export function MemberStatusBadge({ status }: { status: string }): React.ReactElement {
  return <StatusBadge meta={getMemberStatusMeta(status)} />;
}

// ── Invoice status (Stripe: paid / open / draft / void / uncollectible) ────

const INVOICE_STATUS_META: Record<string, KnownMeta> = {
  paid: { label: msg`Pagata`, tone: "success", icon: CircleCheck },
  open: { label: msg`Da pagare`, tone: "warning", icon: Clock },
  draft: { label: msg`Bozza`, tone: "neutral", icon: CircleDashed },
  void: { label: msg`Annullata`, tone: "neutral", icon: CircleX },
  uncollectible: { label: msg`Non riscuotibile`, tone: "danger", icon: CircleAlert },
};

/** Exported for tests — never returns ``undefined``. */
export function getInvoiceStatusMeta(status: unknown): StatusMeta {
  return resolveStatusMeta(INVOICE_STATUS_META, status);
}

export function InvoiceStatusBadge({ status }: { status: string }): React.ReactElement {
  return <StatusBadge meta={getInvoiceStatusMeta(status)} />;
}

// ── Agentic step status (Playground / Assist execution trail) ──────────────
//
// Deliberately THINNER than the vocabularies above: the raw step token is
// shown verbatim (mono, no icon) and any non-terminal state collapses to a
// ``warning`` tone. It resolves neither a Lingui label nor an icon, so it does
// NOT flow through ``StatusBadge`` / ``resolveStatusMeta`` (those add an icon
// and a neutral unknown fallback) — kept here only so status chips share one
// home.

/** Exported for tests — ``ok``→success, ``error``→danger, everything else→warning. */
export function getStepStatusMeta(status: string): ConBadgeTone {
  return status === "ok" ? "success" : status === "error" ? "danger" : "warning";
}

export function StepStatusBadge({ status }: { status: string }): React.ReactElement {
  return (
    <ConBadge tone={getStepStatusMeta(status)}>
      <span className="font-mono">{status}</span>
    </ConBadge>
  );
}
